import React from "react";
import HomePropertyCard from "./HomePropertyCard.js";
import ICON from "../icons.js";

/* ============================================================================
 * HomeFeatured — "ห้องประกาศขายและให้เช่า" : กริดการ์ดห้อง
 *
 * เป็นส่วนแรกที่ผู้ใช้เห็นถัดจากแบนเนอร์ ตัวกรองหลักอยู่ในกล่องค้นหาด้านบนแล้ว
 * ที่หัวส่วนนี้มีแค่ "สถานะ" กับ "เรียงตาม" (backend รองรับมานานแต่ไม่เคยมี UI)
 *
 * ★ โหลดทีละหน้า (page_size 12) + ปุ่ม "โหลดเพิ่ม"
 *   เดิมส่ง page=0 = ขอทุกห้องพร้อมรูปครบในครั้งเดียว โครงการใหญ่ ๆ หน้าแรกช้ามาก
 * ★ โหลดไม่สำเร็จต้องบอกว่า "โหลดไม่สำเร็จ" ไม่ใช่ "ไม่พบห้อง" — คนละความหมายกัน
 *   (ขึ้นว่าไม่พบห้อง ผู้ใช้จะเข้าใจว่าโครงการนี้ไม่มีห้องว่าง แล้วออกไปเลย)
 * ========================================================================== */

// ค่าตรงกับ sortMap ฝั่ง backend (sale-public.service.js) — "" = ลำดับเริ่มต้นตามเลขห้อง
const SORT_OPTIONS = [
    { value: "", label: "เรียงตามเลขห้อง" },
    { value: "newest", label: "ลงประกาศล่าสุด" },
    { value: "price_asc", label: "ราคาขาย ต่ำ → สูง", type: "ขาย" },
    { value: "price_desc", label: "ราคาขาย สูง → ต่ำ", type: "ขาย" },
    { value: "rent_asc", label: "ค่าเช่า ต่ำ → สูง", type: "เช่า" },
    { value: "rent_desc", label: "ค่าเช่า สูง → ต่ำ", type: "เช่า" },
    { value: "popular", label: "เข้าชมมากที่สุด" },
];

// ประกาศเช่ากรองด้วย rent_status ที่เหลือกรองด้วย selling_status (backend เลือกคอลัมน์ตาม listing_type)
export const statusOptions = (listingType) => [
    { value: "", label: "ทุกสถานะ" },
    { value: "ว่าง", label: "ว่าง" },
    { value: "จอง", label: "ติดจอง" },
    listingType === "เช่า" ? { value: "ให้เช่าแล้ว", label: "ให้เช่าแล้ว" } : { value: "ขายแล้ว", label: "ขายแล้ว" },
];

export default function HomeFeatured({
    items = [],
    count = 0,
    loading,
    loadingMore,
    hasMore,
    error,
    filters = {},
    onFilterChange,
    onLoadMore,
    onRetry,
    onOpen,
    onInquiry,
    onClear,
    title,
}) {
    // ประเภทที่เลือกไว้เป็น "ขาย" ไม่ต้องโชว์ตัวเลือกเรียงค่าเช่า (และกลับกัน)
    const sorts = SORT_OPTIONS.filter((o) => !o.type || !filters.listing_type || o.type === filters.listing_type);

    return (
        <section className="hm-section" id="hm-featured">
            <div className="hm-container">
                <div className="hm-section__head">
                    <div>
                        <h2 className="hm-section__title">{title || "ห้องประกาศขายและให้เช่า"}</h2>
                        <p className="hm-section__desc" aria-live="polite">
                            {loading ? "กำลังค้นหา..." : `${Number(count || 0).toLocaleString("th-TH")} ห้อง`}
                        </p>
                    </div>
                    {!!onFilterChange && (
                        <div className="hm-toolbar">
                            <label className="hm-toolbar__field">
                                <span className="visually-hidden">สถานะห้อง</span>
                                <select value={filters.status || ""} onChange={(e) => onFilterChange("status", e.target.value)}>
                                    {statusOptions(filters.listing_type).map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="hm-toolbar__field">
                                <span className="visually-hidden">เรียงลำดับ</span>
                                <select value={filters.sort || ""} onChange={(e) => onFilterChange("sort", e.target.value)}>
                                    {sorts.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-center py-5 hm-muted">กำลังโหลดข้อมูลห้อง...</div>
                ) : error && !items.length ? (
                    <div className="hm-soon" role="alert">
                        <span className="hm-soon__icon hm-ico">{ICON.info}</span>
                        <div className="hm-soon__title">โหลดรายการห้องไม่สำเร็จ</div>
                        <div className="hm-soon__desc">{error}</div>
                        {!!onRetry && (
                            <button type="button" className="hm-btn hm-btn--primary hm-btn--sm mt-3" onClick={onRetry}>
                                ลองอีกครั้ง
                            </button>
                        )}
                    </div>
                ) : items.length ? (
                    <>
                        <div className="hm-grid">
                            {items.map((item) => (
                                <HomePropertyCard key={item.uid} item={item} onOpen={onOpen} onInquiry={onInquiry} />
                            ))}
                        </div>
                        {!!error && (
                            <div className="hm-alert mt-3" role="alert">
                                {error}
                            </div>
                        )}
                        {hasMore && (
                            <div className="text-center mt-3">
                                <button
                                    type="button"
                                    className="hm-btn hm-btn--ghost"
                                    disabled={loadingMore}
                                    onClick={onLoadMore}
                                >
                                    {loadingMore ? "กำลังโหลด..." : `โหลดเพิ่ม (เหลืออีก ${Number(count - items.length).toLocaleString("th-TH")} ห้อง)`}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="hm-soon">
                        <span className="hm-soon__icon hm-ico">{ICON.building}</span>
                        <div className="hm-soon__title">ยังไม่พบห้องที่ตรงกับเงื่อนไข</div>
                        <div className="hm-soon__desc">ลองเปลี่ยนช่วงราคา สถานะ หรือประเภทประกาศ แล้วค้นหาอีกครั้ง</div>
                        <button type="button" className="hm-btn hm-btn--primary hm-btn--sm mt-3" onClick={onClear}>
                            ล้างตัวกรองทั้งหมด
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}
